// GENERATED from internal/config/settings_schema.go. Do not edit.
// Regenerate: go test ./internal/config -run TestSettingsSchemaJS -update

export const SETTING_SECTIONS = [
  {
    "id": "background",
    "title": "Background",
    "desc": "The small process that keeps track of leases and messages on this machine."
  },
  {
    "id": "coordination",
    "title": "Coordination",
    "desc": "How chats reserve files and hand them back."
  },
  {
    "id": "protection",
    "title": "Protection",
    "desc": "Extra safety nets around edits and commits."
  },
  {
    "id": "workspaces",
    "title": "Workspaces",
    "desc": "A separate git worktree for each new chat, so parallel work never mixes."
  },
  {
    "id": "repo_context",
    "title": "Repo context",
    "desc": "What a chat is told about the repo when it starts."
  },
  {
    "id": "history",
    "title": "History and storage",
    "desc": "How much activity, notes and session history this machine keeps."
  },
  {
    "id": "advanced",
    "title": "Advanced",
    "desc": "Logging and how chats find each other."
  }
];

export const SETTING_SPECS = [
  {
    "key": "auto_start",
    "section": "background",
    "label": "Start when you log in",
    "desc": "Starts AgentCollision automatically so your chats are always protected.",
    "tip": "Installs a login item (launchd on macOS, a systemd user unit on Linux). Without it, chats are unprotected until you run agc daemon.",
    "type": "bool",
    "applies": "now",
    "machine": true,
    "default": true
  },
  {
    "key": "telemetry",
    "section": "background",
    "label": "Share anonymous usage stats",
    "desc": "Counts of events only. Never file paths, code or names.",
    "tip": "Helps us see which features are used. Run agc telemetry explain to see exactly what is sent.",
    "type": "bool",
    "applies": "now",
    "machine": true,
    "default": false
  },
  {
    "key": "dashboard_port",
    "section": "background",
    "label": "Dashboard address",
    "desc": "Where this dashboard is served.",
    "tip": "Chosen when the daemon starts (7777, or the next free port up to 7790). Change it with agc daemon --http.",
    "type": "int",
    "applies": "restart",
    "read_only": true,
    "default": 7777
  },
  {
    "key": "track_reads",
    "section": "coordination",
    "label": "Show chats that are only reading",
    "desc": "List every active chat, not just the ones editing right now.",
    "tip": "Reads never block anyone. This only affects what the dashboard and peer lists show.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "allow_revoke",
    "section": "coordination",
    "label": "Allow taking back a lease",
    "desc": "Lets you force-release a file another chat holds, from the dashboard or agc lease revoke.",
    "tip": "Useful when a chat crashed while holding files. The chat that loses the lease is told on its next tool call.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "lease_ttl_secs",
    "section": "coordination",
    "label": "Lease length",
    "desc": "How long a chat holds a file before the lease expires on its own.",
    "tip": "Active chats renew their leases automatically, so this mostly matters for chats that stop without releasing.",
    "type": "int",
    "unit": "seconds",
    "min": 60,
    "max": 86400,
    "applies": "now",
    "default": 1800
  },
  {
    "key": "cooldown_secs",
    "section": "coordination",
    "label": "Hand-back pause",
    "desc": "After a chat releases a file, wait this long before another chat can take it.",
    "tip": "Stops two chats from grabbing the same file back and forth. 0 hands it over immediately.",
    "type": "int",
    "unit": "seconds",
    "min": 0,
    "max": 300,
    "applies": "now",
    "zero_means": "Immediately",
    "default": 15
  },
  {
    "key": "orphan_threshold_secs",
    "section": "coordination",
    "label": "Stale chat after",
    "desc": "A chat that sends no heartbeat for this long is treated as gone and its files freed.",
    "tip": "Covers chats whose window was closed or whose process was killed without releasing.",
    "type": "int",
    "unit": "seconds",
    "min": 30,
    "max": 3600,
    "applies": "now",
    "default": 600
  },
  {
    "key": "watch_unmanaged_writes_enabled",
    "section": "protection",
    "label": "Watch for edits outside a chat",
    "desc": "Notices when a leased file is changed by something that is not the chat holding it.",
    "tip": "For example your editor, a script, or a tool AgentCollision isn't connected to. In a folder several chats share, it also notes changes to files nobody holds. Detection only: it records the event, it cannot undo the write.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "auto_install_pre_commit_hook",
    "section": "protection",
    "label": "Block commits of leased files",
    "desc": "Adds a git pre-commit check to each repo that refuses to commit a file another chat holds.",
    "tip": "Protects work from tools AgentCollision isn't connected to (manual git, Aider, CI). It adds a check to each repo's .git/hooks and keeps any check already there.",
    "type": "bool",
    "applies": "now",
    "default": false
  },
  {
    "key": "worktree_alloc_enabled",
    "section": "workspaces",
    "label": "Give each new chat its own worktree",
    "desc": "Every fresh chat in a repo starts on its own branch in its own folder.",
    "tip": "Parallel chats then cannot stage each other's files or mix commits. Resumed chats and subagents keep their current folder.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "worktree_alloc_repos",
    "section": "workspaces",
    "label": "Which repos",
    "desc": "* for every repo, or a comma-separated list of repo names.",
    "tip": "A repo can also opt out with its own .agentcollision/worktrees.toml.",
    "type": "text",
    "applies": "now",
    "default": "*"
  },
  {
    "key": "worktree_alloc_base_dir",
    "section": "workspaces",
    "label": "Worktree folder",
    "desc": "Where new worktrees are created. Empty uses ~/ac-worktrees.",
    "type": "text",
    "applies": "now",
    "default": ""
  },
  {
    "key": "worktree_alloc_branch_prefix",
    "section": "workspaces",
    "label": "Branch prefix",
    "desc": "New worktree branches start with this.",
    "type": "text",
    "applies": "now",
    "default": "ac/"
  },
  {
    "key": "worktree_gc_enabled",
    "section": "workspaces",
    "label": "Clean up finished worktrees",
    "desc": "Removes a chat's worktree when the chat ends, and old ones when disk runs low.",
    "tip": "A chat that is still open keeps its worktree until it has been idle for 48 hours (or 1 hour when worktrees are over the disk limit). The branch is always kept, so commits are never lost.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "worktree_preserve_dirty",
    "section": "workspaces",
    "label": "Keep unsaved work",
    "desc": "Never remove a worktree that has uncommitted changes.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "worktree_max_disk_gb",
    "section": "workspaces",
    "label": "Disk limit for worktrees",
    "desc": "Cleanup starts removing the oldest worktrees past this size.",
    "tip": "0 picks a limit for this machine: 30% of free disk, between 5 and 100 GB.",
    "type": "int",
    "unit": "GB",
    "min": 0,
    "max": 500,
    "applies": "now",
    "zero_means": "Auto",
    "default": 0
  },
  {
    "key": "git_context_enabled",
    "section": "repo_context",
    "label": "Brief chats on recent repo activity",
    "desc": "When a chat starts, tell it what changed recently and who else is working.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "git_context_detail",
    "section": "repo_context",
    "label": "Detail",
    "desc": "How much the briefing includes.",
    "tip": "Minimal: branch and a few commits. Standard: plus busy files and other worktrees. Full: everything, including merged pull requests.",
    "type": "enum",
    "options": [
      {
        "value": "minimal",
        "label": "Minimal"
      },
      {
        "value": "standard",
        "label": "Standard"
      },
      {
        "value": "full",
        "label": "Full"
      }
    ],
    "applies": "now",
    "default": "standard"
  },
  {
    "key": "git_context_lookback_hours",
    "section": "repo_context",
    "label": "Look back",
    "desc": "How far back the briefing goes.",
    "type": "int",
    "unit": "hours",
    "min": 1,
    "max": 168,
    "applies": "now",
    "default": 24
  },
  {
    "key": "git_context_max_commits",
    "section": "repo_context",
    "label": "Commits to list",
    "desc": "The most recent commits included in the briefing.",
    "type": "int",
    "min": 1,
    "max": 100,
    "applies": "now",
    "default": 15
  },
  {
    "key": "git_context_churn_threshold",
    "section": "repo_context",
    "label": "Busy file after",
    "desc": "A file changed this many times in the window is flagged as busy.",
    "type": "int",
    "unit": "changes",
    "min": 2,
    "max": 50,
    "applies": "now",
    "default": 3
  },
  {
    "key": "git_context_include_prs",
    "section": "repo_context",
    "label": "Include merged pull requests",
    "desc": "List recently merged PRs (uses the gh CLI when available).",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "git_context_include_worktrees",
    "section": "repo_context",
    "label": "Include other worktrees",
    "desc": "Say which other worktrees of the repo have active chats.",
    "type": "bool",
    "applies": "now",
    "default": true
  },
  {
    "key": "git_context_timeout_secs",
    "section": "repo_context",
    "label": "Time limit",
    "desc": "Skip the briefing if gathering it takes longer than this.",
    "type": "int",
    "unit": "seconds",
    "min": 1,
    "max": 30,
    "applies": "now",
    "default": 3
  },
  {
    "key": "event_retention_days",
    "section": "history",
    "label": "Keep activity for",
    "desc": "Older activity (lease grants, releases, conflicts) is deleted, oldest first.",
    "tip": "Messages between chats are kept separately and are not affected. 0 keeps everything.",
    "type": "int",
    "unit": "days",
    "min": 0,
    "applies": "restart",
    "zero_means": "Forever",
    "default": 30
  },
  {
    "key": "event_max_rows",
    "section": "history",
    "label": "Activity size cap",
    "desc": "Also delete the oldest activity past this many events.",
    "tip": "A busy machine records about 130,000 events a day. 0 removes the cap.",
    "type": "int",
    "unit": "events",
    "min": 0,
    "applies": "restart",
    "zero_means": "No cap",
    "default": 500000
  },
  {
    "key": "notes_max_live",
    "section": "history",
    "label": "Team notes to keep",
    "desc": "Notes per topic on the pinned board. At the limit the oldest note is replaced.",
    "type": "int",
    "unit": "notes",
    "min": 0,
    "applies": "now",
    "zero_means": "No limit",
    "default": 500
  },
  {
    "key": "agent_reap_hours",
    "section": "history",
    "label": "Forget idle sessions after",
    "desc": "Remove a chat from the session list after this long with no activity.",
    "type": "int",
    "unit": "hours",
    "min": 0,
    "applies": "restart",
    "zero_means": "Never",
    "default": 24
  },
  {
    "key": "decision_capture_files_threshold",
    "section": "history",
    "label": "Record a session summary after",
    "desc": "Write a summary to Coordination when a session touched more than this many files...",
    "type": "int",
    "unit": "files",
    "min": 0,
    "applies": "now",
    "default": 2
  },
  {
    "key": "decision_capture_duration_secs",
    "section": "history",
    "label": "...or ran longer than",
    "desc": "...or when it ran longer than this.",
    "type": "int",
    "unit": "seconds",
    "min": 0,
    "applies": "now",
    "default": 120
  },
  {
    "key": "spend_proxy_enabled",
    "section": "advanced",
    "label": "Run the spend proxy",
    "desc": "Listen on 127.0.0.1:11434 so agents that send API calls through it get per-chat spend.",
    "tip": "Off by default. Claude Code spend is read from its own session logs and needs no proxy. Turn this on only for an agent you point at the proxy with ANTHROPIC_BASE_URL. Applies when the background service restarts.",
    "type": "bool",
    "applies": "restart",
    "default": false
  },
  {
    "key": "log_level",
    "section": "advanced",
    "label": "Log detail",
    "desc": "How much AgentCollision writes to its log file (daemon.log).",
    "type": "enum",
    "options": [
      {
        "value": "error",
        "label": "Errors"
      },
      {
        "value": "warn",
        "label": "Warnings"
      },
      {
        "value": "info",
        "label": "Normal"
      },
      {
        "value": "debug",
        "label": "Debug"
      }
    ],
    "applies": "now",
    "default": "info"
  },
  {
    "key": "who_default_scope",
    "section": "advanced",
    "label": "Chats a chat can see",
    "desc": "Which peers agc who and the start-of-chat briefing list by default.",
    "tip": "This repo: chats in any worktree of the same repo. Everywhere: every active chat on the machine. --all always shows everything.",
    "type": "enum",
    "options": [
      {
        "value": "repo",
        "label": "This repo"
      },
      {
        "value": "global",
        "label": "Everywhere"
      }
    ],
    "applies": "now",
    "default": "repo"
  }
];
